import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_ROUND_SECONDS, ROUND_RESULT_SECONDS } from '../config';
import { MOVE_BUTTONS, moveEmoji, outcomeSpec } from '../lib/moves';
import { truncateName } from '../lib/playerId';
import { useJsonSocket } from '../lib/useJsonSocket';
import type {
  GameMessage,
  GameRequest,
  MatchDetails,
  Move,
  PlayedMove,
  RoundOutcome,
  Scores,
} from '../protocol';

export interface GameSummary {
  iWon: boolean;
  reason?: string;
  finalScores: Scores;
  myName: string;
  oppName: string;
}

interface Props {
  playerId: string;
  match: MatchDetails;
  onGameOver: (summary: GameSummary) => void;
}

type Tone = 'neutral' | 'accent' | 'good' | 'warn' | 'bad';
type Phase = 'connecting' | 'waiting' | 'choosing' | 'roundResult' | 'ended';

interface Status {
  text: string;
  tone: Tone;
}

interface RoundView {
  result: RoundOutcome;
  youPlayed: PlayedMove;
  opponentPlayed: PlayedMove;
}

interface RoundTimer {
  /** Round length in seconds, already net of the result-display window. */
  limit: number;
  /** Wall-clock deadline; the bar interpolates against this. */
  deadline: number;
}

const ZERO_SCORES: Scores = { you: 0, opponent: 0 };

/**
 * The live match. Owns a direct WebSocket to the C++ game server — `wss://` via
 * Caddy on managed fleets, plain `ws://` against the dev Anywhere fleet, which
 * has no TLS or DNS.
 */
export default function GameScreen({ playerId, match, onGameOver }: Props) {
  const [myName, setMyName] = useState(playerId);
  const [oppName, setOppName] = useState('…');
  const [scores, setScores] = useState<Scores>(ZERO_SCORES);
  const [phase, setPhase] = useState<Phase>('connecting');
  const [status, setStatus] = useState<Status>({
    text: 'Connecting to game server…',
    tone: 'neutral',
  });
  const [timer, setTimer] = useState<RoundTimer | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [selectedMove, setSelectedMove] = useState<Move | null>(null);
  const [round, setRound] = useState<RoundView | null>(null);

  // A PROMPT that arrived while the previous round's result is still on screen.
  const pendingPromptRef = useRef<Extract<GameMessage, { type: 'PROMPT' }> | null>(null);
  const sawRoundResultRef = useRef(false);
  const namesRef = useRef({ mine: playerId, opponent: '…' });

  // ── Countdown scheduling ────────────────────────────────────────
  // One place to cancel every pending timeout, so unmount can never leave a
  // timer running against a dead socket.
  const timeoutsRef = useRef<number[]>([]);
  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  const countdown = useCallback(
    (seconds: number, label: (n: number) => string, done: () => void) => {
      const step = (n: number) => {
        if (n <= 0) {
          done();
          return;
        }
        setStatus({ text: label(n), tone: 'accent' });
        timeoutsRef.current.push(window.setTimeout(() => step(n - 1), 1000));
      };
      step(seconds);
    },
    [],
  );

  // ── Round timer ─────────────────────────────────────────────────
  useEffect(() => {
    if (!timer) return;
    let frame = 0;
    const tick = () => {
      const left = Math.max(0, (timer.deadline - Date.now()) / 1000);
      setTimeLeft(left);
      if (left > 0) frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [timer]);

  useEffect(() => {
    if (phase !== 'choosing' || timeLeft > 0 || selectedMove) return;
    setStatus((prev) =>
      prev.text.startsWith("Time's up") ? prev : { text: "Time's up! Waiting for result…", tone: 'warn' },
    );
  }, [phase, timeLeft, selectedMove]);

  // ── Message handling ────────────────────────────────────────────

  const activatePrompt = useCallback((prompt: Extract<GameMessage, { type: 'PROMPT' }>) => {
    // The server's limit covers the whole round including the window where the
    // previous result was on screen, so subtract that to show honest time left.
    const limit = (prompt.time_limit_seconds ?? DEFAULT_ROUND_SECONDS) - ROUND_RESULT_SECONDS;
    setScores(prompt.scores);
    setRound(null);
    setSelectedMove(null);
    setPhase('choosing');
    setStatus({ text: 'Choose your move!', tone: 'accent' });
    setTimeLeft(limit);
    setTimer({ limit, deadline: Date.now() + limit * 1000 });
  }, []);

  const handleOpen = useCallback(() => {
    setStatus({ text: 'Connected! Sending handshake…', tone: 'neutral' });
    send({ psid: match.psess, playerName: playerId });
  }, [match.psess, playerId]); // `send` is created below and is referentially stable

  const handleMessage = useCallback(
    (msg: GameMessage) => {
      switch (msg.type) {
        case 'WELCOME':
          setPhase('waiting');
          setStatus({
            text: `You are Player ${msg.index}. Waiting for match to start…`,
            tone: 'neutral',
          });
          break;

        case 'MATCH_INFO':
          namesRef.current = { mine: msg.your_name, opponent: msg.opponent_name };
          setMyName(msg.your_name);
          setOppName(msg.opponent_name);
          break;

        case 'PROMPT':
          if (sawRoundResultRef.current) {
            // Hold the result on screen for its full window before the next round.
            pendingPromptRef.current = msg;
            countdown(
              ROUND_RESULT_SECONDS,
              (n) => `Next round in ${n}…`,
              () => {
                const pending = pendingPromptRef.current;
                pendingPromptRef.current = null;
                if (pending) activatePrompt(pending);
              },
            );
          } else {
            activatePrompt(msg);
          }
          break;

        case 'ROUND_RESULT':
          sawRoundResultRef.current = true;
          setScores(msg.scores);
          setTimer(null);
          setPhase('roundResult');
          setStatus({ text: '', tone: 'neutral' });
          setRound({
            result: msg.result,
            youPlayed: msg.you_played,
            opponentPlayed: msg.opponent_played,
          });
          break;

        case 'OPPONENT_DISCONNECTED':
          setTimer(null);
          setPhase('waiting');
          setStatus({
            text: `Opponent disconnected — waiting up to ${msg.grace_period_seconds}s for reconnect…`,
            tone: 'warn',
          });
          break;

        case 'OPPONENT_RECONNECTED':
          setStatus({ text: 'Opponent reconnected! Resuming match…', tone: 'good' });
          break;

        case 'GAME_OVER': {
          close();
          setTimer(null);
          setPhase('ended');
          const summary: GameSummary = {
            iWon: msg.i_won,
            reason: msg.reason,
            finalScores: msg.final_scores,
            myName: namesRef.current.mine,
            oppName: namesRef.current.opponent,
          };
          countdown(
            ROUND_RESULT_SECONDS,
            (n) => `Match over in ${n}…`,
            () => onGameOver(summary),
          );
          break;
        }

        case 'ERROR':
          setTimer(null);
          setPhase('ended');
          setStatus({ text: `Server error: ${msg.msg}`, tone: 'bad' });
          break;
      }
    },
    [activatePrompt, countdown, onGameOver], // `close` is referentially stable
  );

  const handleClose = useCallback((reason: 'closed' | 'error') => {
    setTimer(null);
    setPhase('ended');
    setStatus({
      text: reason === 'error' ? 'Could not connect to game server.' : 'Disconnected from server.',
      tone: 'bad',
    });
  }, []);

  const serverUrl = useMemo(
    () => (match.dns ? `wss://${match.dns}:${match.port}` : `ws://${match.ip}:${match.port}`),
    [match.dns, match.ip, match.port],
  );

  const { send, close } = useJsonSocket<GameMessage, GameRequest>({
    url: serverUrl,
    onOpen: handleOpen,
    onMessage: handleMessage,
    onClose: handleClose,
  });

  // ── Interaction ─────────────────────────────────────────────────

  const canMove = phase === 'choosing' && !selectedMove && timeLeft > 0;

  const sendMove = (move: Move) => {
    if (!canMove) return;
    setSelectedMove(move);
    send({ move });
    setStatus({ text: `You played ${move} — waiting for opponent…`, tone: 'neutral' });
  };

  // ── Render ──────────────────────────────────────────────────────

  const ratio = timer ? Math.min(1, Math.max(0, timeLeft / timer.limit)) : 0;
  const barTone = ratio > 0.5 ? 'full' : ratio > 0.2 ? 'mid' : 'low';
  const outcome = round ? outcomeSpec(round.result) : null;

  return (
    <main className="screen screen--game">
      <div className="accent-bar" />

      <section className="scoreboard">
        <div className="scoreboard__side scoreboard__side--you">
          <span className="scoreboard__name">{truncateName(myName)}</span>
          <span className="scoreboard__score">{scores.you}</span>
        </div>
        <span className="scoreboard__vs">VS</span>
        <div className="scoreboard__side scoreboard__side--opp">
          <span className="scoreboard__name">{truncateName(oppName)}</span>
          <span className="scoreboard__score">{scores.opponent}</span>
        </div>
      </section>

      {round && outcome ? (
        <section className={`round-result round-result--${outcome.tone}`}>
          <h2 className="round-result__title">{outcome.title}</h2>
          <div className="round-result__moves">
            <div className="round-result__player">
              <span className="round-result__player-name round-result__player-name--you">
                {truncateName(myName)}
              </span>
              <span className="round-result__move">{moveEmoji(round.youPlayed)}</span>
            </div>
            <span className="round-result__symbol">{outcome.symbol}</span>
            <div className="round-result__player">
              <span className="round-result__player-name round-result__player-name--opp">
                {truncateName(oppName)}
              </span>
              <span className="round-result__move">{moveEmoji(round.opponentPlayed)}</span>
            </div>
          </div>
        </section>
      ) : (
        <section className="timer">
          <span className="timer__label">ROUND TIMER</span>
          <span className={`timer__value ${ratio > 0.25 ? '' : 'timer__value--low'}`}>
            {timer ? `${Math.ceil(timeLeft)}s` : '—'}
          </span>
          <div className="timer__track">
            <div
              className={`timer__fill timer__fill--${barTone}`}
              style={{ width: `${ratio * 100}%` }}
            />
          </div>
        </section>
      )}

      <p className={`status status--${status.tone}`} role="status">
        {status.text}
      </p>

      <section className="moves">
        {MOVE_BUTTONS.map(({ move, emoji, label }) => (
          <button
            key={move}
            type="button"
            className={
              'move' +
              ` move--${move}` +
              (selectedMove === move ? ' move--chosen' : '') +
              (selectedMove && selectedMove !== move ? ' move--dimmed' : '')
            }
            disabled={!canMove}
            onClick={() => sendMove(move)}
          >
            <span className="move__emoji">{emoji}</span>
            <span className="move__label">{label}</span>
          </button>
        ))}
      </section>
    </main>
  );
}
